import React, { useState, useCallback, useRef, useEffect } from 'react';
import CompositeAddress from '../CompositeAddress/CompositeAddress';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';

// ─── Types ────────────────────────────────────────────────────────────────────

type PersonalAddressValue = {
  communication_address_line1?: string;
  communication_address_line2?: string;
  communication_address_line3?: string;
  communication_address_landmark?: string;
  communication_address_pincode?: string;
  residential_address_line1?: string;
  residential_address_line2?: string;
  residential_address_line3?: string;
  residential_address_landmark?: string;
  residential_address_pincode?: string;
  [key: string]: string | undefined;
};

interface PersonalAddressDetailsProps {
  form?: any;
  value?: PersonalAddressValue;
  onChange?: (val: PersonalAddressValue) => void;
  disabled?: boolean;
  readOnly?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const PersonalAddressDetails: React.FC<PersonalAddressDetailsProps> = ({
  form,
  value = {},
  onChange,
  disabled = false,
  readOnly = false,
  registerSubmit,
}) => {
  const [sameAsCommunication, setSameAsCommunication] = useState(false);

  // Hold child submit handlers registered by each CompositeAddress
  const childHandlersRef = useRef<Record<string, () => Promise<boolean>>>({});

  // Register a submit handler that runs all child validators
  useEffect(() => {
    if (!registerSubmit) return;
    registerSubmit(async () => {
      const handlers = Object.values(childHandlersRef.current);
      for (const handler of handlers) {
        const ok = await handler();
        if (!ok) return false;
      }
      return true;
    });
  }, [registerSubmit]);

  // Copy communication → residential fields in the current value object
  const mirrorCommunicationToResidential = useCallback(
    (source: PersonalAddressValue): PersonalAddressValue => ({
      ...source,
      residential_address_line1: source.communication_address_line1 ?? '',
      residential_address_line2: source.communication_address_line2 ?? '',
      residential_address_line3: source.communication_address_line3 ?? '',
      residential_address_landmark: source.communication_address_landmark ?? '',
      residential_address_pincode: source.communication_address_pincode ?? '',
    }),
    []
  );

  const handleSameAsCommunicationChange = useCallback(
    (checked: boolean) => {
      setSameAsCommunication(checked);

      if (!checked) return;

      if (form) {
        const options = { shouldValidate: true, shouldDirty: true };
        form.setValue('residential_address_line1', form.getValues('communication_address_line1') || '', options);
        form.setValue('residential_address_line2', form.getValues('communication_address_line2') || '', options);
        form.setValue('residential_address_line3', form.getValues('communication_address_line3') || '', options);
        form.setValue('residential_address_landmark', form.getValues('communication_address_landmark') || '', options);
        form.setValue('residential_address_pincode', form.getValues('communication_address_pincode') || '', options);
      } else if (onChange) {
        onChange(mirrorCommunicationToResidential(value));
      }
    },
    [form, value, onChange, mirrorCommunicationToResidential]
  );

  // Called when the Communication Address sub-form changes.
  // If checkbox is on, keep residential in sync automatically.
  const handleCommunicationChange = useCallback(
    (updated: PersonalAddressValue) => {
      if (!onChange) return;
      onChange(sameAsCommunication ? mirrorCommunicationToResidential(updated) : updated);
    },
    [onChange, sameAsCommunication, mirrorCommunicationToResidential]
  );

  // Called when the Residential Address sub-form changes (only when checkbox is off).
  const handleResidentialChange = useCallback(
    (updated: PersonalAddressValue) => {
      onChange?.(updated);
    },
    [onChange]
  );

  // The effective value shown in the Residential Address section.
  // When checkbox is on, mirror communication fields into residential keys.
  const effectiveValue: PersonalAddressValue = sameAsCommunication
    ? mirrorCommunicationToResidential(value)
    : value;

  return (
    <div className="space-y-6">
      {/* ── Communication Address ─────────────────────────────────────────── */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide border-b pb-1">
          Communication Address
        </h3>
        <CompositeAddress
          form={form}
          prefix="communication"
          value={value}
          onChange={handleCommunicationChange}
          disabled={disabled}
          readOnly={readOnly}
          registerSubmit={(handler) => { childHandlersRef.current['communication'] = handler; }}
        />
      </div>

      {/* ── "Same as Communication Address" checkbox ──────────────────────── */}
      <div className="flex items-center gap-2 py-2 px-3 bg-gray-50 rounded-lg border border-dashed border-gray-300">
        <Checkbox
          id="same-as-communication"
          checked={sameAsCommunication}
          onCheckedChange={(checked) => handleSameAsCommunicationChange(Boolean(checked))}
          disabled={disabled || readOnly}
        />
        <Label
          htmlFor="same-as-communication"
          className="text-sm font-medium text-gray-700 cursor-pointer select-none"
        >
          Residential address is same as Communication address
        </Label>
      </div>

      {/* ── Residential Address ───────────────────────────────────────────── */}
      <div className="space-y-3">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide border-b pb-1">
          Residential Address
        </h3>
        <CompositeAddress
          form={form}
          prefix="residential"
          value={effectiveValue}
          onChange={handleResidentialChange}
          disabled={disabled || sameAsCommunication}
          readOnly={readOnly || sameAsCommunication}
          registerSubmit={sameAsCommunication ? undefined : (handler) => { childHandlersRef.current['residential'] = handler; }}
        />
      </div>
    </div>
  );
};

export default PersonalAddressDetails;
